// AlertsPanel — actionable business alerts for the cockpit.
// Each alert is clickable, linking to the relevant resource page.

import { Alert as MuiAlert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { t } from '../../i18n';

export type AlertEntry = {
  severity: 'error' | 'warning' | 'info' | 'success';
  title: string;
  detail?: string;
  description?: string;
  action?: string;
  to: string; // nav route to drill into
};

export function AlertsPanel({ alerts }: { alerts: AlertEntry[] }) {
  const navigate = useNavigate();

  if (!alerts.length) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('shell.noAlerts')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {alerts.map((alert, i) => (
        <MuiAlert
          key={i}
          severity={alert.severity}
          variant="outlined"
          sx={{ '& .MuiAlert-message': { width: '100%' } }}
          action={
            <Button size="small" color="inherit" onClick={() => navigate(alert.to)} sx={{ whiteSpace: 'nowrap' }}>
              {t('action.view')}
            </Button>
          }
        >
          <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>{alert.title}</AlertTitle>
          <Typography variant="body2">{alert.detail ?? alert.description}</Typography>
          {alert.action ? <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>{alert.action}</Typography> : null}
        </MuiAlert>
      ))}
    </Stack>
  );
}
